// PDI Building Suggestion — Forma extension frontend (v3: corrected field paths)
import { Forma } from "forma-embedded-view-sdk/auto";

const BACKEND_URL = "https://pdi-forma-backend.pdi-jlg.workers.dev";

const statusEl = document.getElementById("status")!;
const resultsEl = document.getElementById("results")!;
const categorySelect = document.getElementById("category") as HTMLSelectElement;
const runBtn = document.getElementById("runScoreBtn") as HTMLButtonElement;
const selectionInfoEl = document.createElement("div");
selectionInfoEl.style.cssText = "font-size:11px;color:#0b6bcb;margin-bottom:8px;";
statusEl.after(selectionInfoEl);

function setStatus(msg: string) {
  statusEl.textContent = msg;
}

/** Sums the .value of every entry in a functionBreakdown array (or returns
 * 0 if the metric/array isn't present). This is how Forma reports GFA,
 * site area, building coverage, etc. — broken down by function/use, not
 * as one flat number. */
function sumFunctionBreakdown(metric: any): number {
  if (!metric || !Array.isArray(metric.functionBreakdown)) return 0;
  return metric.functionBreakdown.reduce((sum: number, f: any) => sum + (f.value || 0), 0);
}

async function getSelectedBuildingCandidate() {
  const selection = await Forma.selection.getSelection();

  if (!selection || selection.length === 0) {
    selectionInfoEl.textContent = "No building selected — click a building in the 3D view first.";
    return null;
  }

  const metrics: any = await Forma.areaMetrics.calculate({ paths: selection });
  console.log("Raw areaMetrics result:", metrics);

  const built = metrics?.builtInMetrics || {};

  const gfa = sumFunctionBreakdown(built.grossFloorArea);
  const siteArea = sumFunctionBreakdown(built.siteArea);
  const buildingCoverage = sumFunctionBreakdown(built.buildingCoverage);
  const unitCount = metrics?.unitStatistics?.count?.value ?? 0;
  const parkingSpots = metrics?.parkingStatistics?.spots ?? 0;

  selectionInfoEl.textContent =
    `Reading ${selection.length} selected element(s) — ` +
    `GFA: ${gfa.toLocaleString()} m², Units: ${unitCount}, Parking spots: ${parkingSpots}`;

  // NOTE: floor count and floor-to-floor height are NOT part of areaMetrics
  // — they belong to the building element's own properties (visible in the
  // native "Building" panel as "29" floors / "3m"). We still need to find
  // and wire that specific API call; until then these two + daylight/noise
  // remain estimates, clearly marked below.
  return {
    name: `Selected building (${selection.length} element${selection.length > 1 ? "s" : ""})`,
    gfa,
    siteArea,
    buildingCoverage,
    unitCount,
    parkingSpots,
    // TODO: replace with real building.floors / building.floorToFloorHeight
    // once we find the correct Forma SDK call (likely Forma.geometry or a
    // building-specific properties API — not areaMetrics).
    floors: 0,
    floorToFloorHeightM: 3.0,
    // TODO: pull from Sun/Daylight analysis (Forma.analysis.* )
    daylightHoursLivingRoom: 3.0,
    // TODO: pull from Noise analysis
    facadeNoiseDb: 60,
    // Can be estimated once real NIA is available: nia / gfa
    nsaEfficiency: gfa > 0 ? 0.75 : 0,
    // parkingSpots / unitCount once units are populated (currently 0 units
    // on this Site Design building, so ratio is not yet meaningful)
    parkingRatioPerUnit: unitCount > 0 ? parkingSpots / unitCount : 0,
  };
}

function renderResults(ranked: any[]) {
  resultsEl.innerHTML = "";
  ranked.forEach((r, i) => {
    const scoreClass = r.score >= 80 ? "good" : r.score >= 50 ? "warn" : "bad";
    const card = document.createElement("div");
    card.className = "card";
    card.innerHTML = `
      <h3>#${i + 1} ${r.name} <span style="float:right" class="score ${scoreClass}">${r.score}%</span></h3>
      <div style="font-size:12px;color:#666;">${r.categoryLabel} — ${r.passedChecks}/${r.totalChecks} rules passed</div>
      <div class="checks">
        ${r.checks
          .map(
            (c: any) => `
          <div class="check-row">
            <span>${c.rule}</span>
            <span class="${c.pass ? "pass" : "fail"}">${c.value} (target ${c.target})</span>
          </div>`
          )
          .join("")}
      </div>
    `;
    resultsEl.appendChild(card);
  });
}

async function runScoring() {
  runBtn.disabled = true;
  setStatus("Reading selected building from Forma...");

  try {
    const selected = await getSelectedBuildingCandidate();

    if (!selected) {
      setStatus("Please select a building in the 3D view, then try again.");
      runBtn.disabled = false;
      return;
    }

    setStatus("Scoring against PDI Playbook...");
    const resp = await fetch(`${BACKEND_URL}/score`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ category: categorySelect.value, candidates: [selected] }),
    });

    if (!resp.ok) throw new Error(`Backend error: ${resp.status} ${await resp.text()}`);

    const data = await resp.json();
    renderResults(data.ranked);
    setStatus(`Done. Scored the selected building against Category ${categorySelect.value}.`);
  } catch (err: any) {
    console.error(err);
    setStatus(`Error: ${err.message}`);
  } finally {
    runBtn.disabled = false;
  }
}

runBtn.addEventListener("click", runScoring);

setStatus("Ready. Select a building in the 3D view, then click 'Score current proposal'.");
